/**
 * Pure widget-density classification (IB-311 Slice 1 — "self-adjusting" dashboard). No DOM, no
 * React — a plain function of a measured box so the rule is exhaustively unit-testable independent
 * of `ResizeObserver`/jsdom (see `density.test.ts`).
 *
 * Three tiers, matching design-system.md §5 "auto-density":
 *   - `full`    — plenty of room: legends, axis labels, gridlines, KPI captions/deltas, all shown.
 *   - `compact` — legends/gridlines drop, axis labels thin out, KPI tiles lose their caption, long
 *                 category lists truncate to a top-N + "+N more" note.
 *   - `micro`   — collapses to the smallest honest representation: a KPI tile shows the number only;
 *                 a chart collapses to a sparkline + one summary stat (never a description-less blank).
 */

export type WidgetDensity = 'full' | 'compact' | 'micro'

export interface DensityThresholds {
  compactBelowWidth: number
  microBelowWidth: number
  compactBelowHeight: number
  microBelowHeight: number
}

/** Tuned against the 12-col grid's own span/minHeight choices (`dashboardWidgetRegistry.tsx`) —
 * a hero KPI cell (≈260×140) lands `compact`; a full chart cell narrowed to one mobile column
 * (≈320×280) stays `full`; a chart squeezed to a third of a row on a laptop (≈220×220) goes `micro`. */
export const DEFAULT_DENSITY_THRESHOLDS: DensityThresholds = {
  compactBelowWidth: 360,
  microBelowWidth: 240,
  compactBelowHeight: 220,
  microBelowHeight: 160,
}

/**
 * Classifies a measured `(width, height)` box. `0×0` (not yet measured — the very first render,
 * before `ResizeObserver`'s first callback, or any environment with no `ResizeObserver` at all e.g.
 * most component tests) is treated as `full`: assume roomy until proven otherwise, so nothing ever
 * flashes a degraded micro view before it has actually been measured.
 */
export function classifyDensity(
  width: number,
  height: number,
  thresholds: DensityThresholds = DEFAULT_DENSITY_THRESHOLDS,
): WidgetDensity {
  if (width <= 0 && height <= 0) return 'full'
  if (width < thresholds.microBelowWidth || height < thresholds.microBelowHeight) return 'micro'
  if (width < thresholds.compactBelowWidth || height < thresholds.compactBelowHeight) return 'compact'
  return 'full'
}
